import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { getHomePathForRole } from "../../utils/rolePaths";
import { NAV_LINKS, SITE_LOGO } from "../data/siteContent";

function scrollTo(href) {
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { token, user, roleFromToken } = useAuth();
  const role = user?.role || roleFromToken;
  const appPath = role ? getHomePathForRole(role) : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-ink/90 py-3 shadow-lg backdrop-blur-xl"
          : "bg-gradient-to-b from-ink/80 to-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          href="#home"
          onClick={(e) => {
            e.preventDefault();
            scrollTo("#home");
          }}
          className="group flex shrink-0 items-center"
        >
          <img
            src={SITE_LOGO}
            alt="Inception Design Studio"
            className="h-10 w-auto object-contain transition group-hover:opacity-90 sm:h-12"
          />
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollTo(link.href);
              }}
              className="font-sans text-sm font-medium text-white/65 transition hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {token && appPath && appPath !== "/login" ? (
            <Link
              to={appPath}
              className="rounded-full border border-white/20 px-5 py-2.5 font-sans text-sm font-medium text-white transition hover:border-accent hover:bg-white/5"
            >
              Dashboard
            </Link>
          ) : null}
          <Link
            to="/login"
            className="rounded-full bg-gradient-to-r from-accent via-indigo-500 to-coral/90 px-6 py-2.5 font-sans text-sm font-semibold text-white shadow-glow transition hover:brightness-110"
          >
            Sign In
          </Link>
        </div>

        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 font-mono text-lg text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? "✕" : "☰"}
        </button>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="rounded-lg px-3 py-3 font-sans text-sm font-medium text-white/80 hover:bg-white/5"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(link.href);
                    setOpen(false);
                  }}
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/login"
                className="mt-2 rounded-full bg-gradient-to-r from-accent to-coral py-3 text-center font-sans text-sm font-semibold text-white"
                onClick={() => setOpen(false)}
              >
                Sign In
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
